import { useState, useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { converters } from "@/lib/firestore";
import type { Org } from "@/types/firestore";

export interface OrgWithId extends Org {
  id: string;
}

interface OrgState {
  orgId: string | undefined;
  org: OrgWithId | null;
  loading: boolean;
  error: string | null;
}

function initialState(orgId: string | undefined): OrgState {
  return { orgId, org: null, loading: !!orgId, error: null };
}

export function useOrg(orgId: string | undefined) {
  const [state, setState] = useState<OrgState>(() => initialState(orgId));

  useEffect(() => {
    let cancelled = false;
    setState(initialState(orgId));

    if (!orgId) {
      return;
    }

    const ref = doc(db, "orgs", orgId).withConverter(converters.orgs);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (cancelled) return;
        setState({
          orgId,
          org: snap.exists()
            ? ({ id: snap.id, ...snap.data() } as OrgWithId)
            : null,
          loading: false,
          error: null,
        });
      },
      (err) => {
        if (cancelled) return;
        console.error("Error fetching org:", err);
        setState({
          orgId,
          org: null,
          loading: false,
          error: "Failed to load organization.",
        });
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [orgId]);

  const isCurrent = state.orgId === orgId;
  return {
    org: isCurrent ? state.org : null,
    loading: isCurrent ? state.loading : !!orgId,
    error: isCurrent ? state.error : null,
  };
}
